import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import MoreButton from 'components/buttons/more-button/MoreButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { deleteList, downloadList } from 'modules/lists/reducers/lists';

import BulkAddToShortlistModal from 'modules/shortlist/components/BulkAddToShortlistModal';
import CollectionsBulkDeleteCreatorsModal from 'modules/lists/components/modals/bulk-delete-creators/CollectionsBulkDeleteCreatorsModal';
import CollectionDuplicateModal from 'modules/lists/components/modals/duplicate/CollectionDuplicateModal';

import { __t, applyTranslations } from 'utils/language';
import { CREATORS, STATS, insertIf } from 'utils/list';

import type { List, ListConfirmModalProps, ListData, ListType, ListsStatsView, LocationWindow } from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./lists-context-menu.i18n.json'));

type OwnProps = {
  orgId: number,
  list: List,
  canManageList: boolean,
  isManageableByOrganization?: boolean,
  view?: ListsStatsView,
  location?: LocationWindow,
  onDelete: () => void,
  onDuplicate?: () => void,
  onShowStats?: (a: string) => void,
  onShowCreators?: () => void
};

type DispatchProps = {
  dispatchDownloadList: (orgId: number, listId: number, label: string, type: ListType) => void,
  dispatchDeleteList: (orgId: number, listId: number) => Promise<void>,
  dispatchShowConfirmationModal: (props: ListConfirmModalProps) => void,
  dispatchHideConfirmationModal: () => void
};

type Props = OwnProps & DispatchProps;

const ContextMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: true
});

class ListsContextMenu extends React.PureComponent<Props> {
  getContextMenuItems(): Array<any> {
    const { orgId, list, canManageList, view, location, onShowCreators, onShowStats, isManageableByOrganization, onDuplicate } = this.props;
    const hasCreators = (list && list.numCreators || 0) > 0;

    return [
      ...insertIf(view === CREATORS && hasCreators, {
        name: 'switch-to-stats-view',
        text: __t('lists_context_menu.view_stats'),
        onClick: () => onShowStats && onShowStats('context-menu')
      }),
      ...insertIf(view === STATS, {
        name: 'switch-to-creators-view',
        text: __t('lists_context_menu.view_collection'),
        onClick: onShowCreators
      }),
      ...insertIf(view && hasCreators, {
        type: 'separator'
      }),
      ...insertIf(canManageList, {
        type: 'edit_list',
        name: 'list-edit',
        text: __t('lists_context_menu.edit'),
        meta: {
          list: {
            orgId,
            listId: list.id,
            title: __t('lists_context_menu.modal.title_edit'),
            label: list.label,
            description: list.description,
            isEditMode: true
          }
        }
      }),
      ...insertIf(hasCreators, {
        name: 'push-pin-custom-bg',
        type: 'modal',
        text: __t('lists_context_menu.add_to_shortlist'),
        meta: {
          list,
          organizationId: list.organization?.id
        },
        extra: {
          component: BulkAddToShortlistModal
        }
      }),
      ...insertIf(hasCreators, {
        name: 'download-list',
        text: __t('lists_context_menu.download'),
        onClick: this.handleDownloadList
      }),
      ...insertIf(hasCreators && canManageList, {
        name: 'bulk-delete-creators',
        type: 'modal',
        text: __t('lists_context_menu.remove_multiple_creators'),
        meta: {
          list,
          location,
          orgId: list.organization?.id
        },
        extra: {
          component: CollectionsBulkDeleteCreatorsModal
        }
      }),
      ...insertIf(hasCreators && isManageableByOrganization, {
        name: 'duplicate-collection',
        type: 'modal',
        text: __t('lists_context_menu.duplicate_collection'),
        meta: {
          collectionId: list.id,
          currentOrgId: list.organization?.id,
          onDuplicate
        },
        extra: {
          component: CollectionDuplicateModal
        }
      }),
      ...insertIf(canManageList, {
        name: 'delete-list',
        text: __t('lists_context_menu.delete_list'),
        onClick: this.handleDeleteList
      })
    ];
  }

  handleResidualClick = (event: SyntheticEvent<HTMLElement>): void => {
    event.stopPropagation();
  };

  handleDownloadList = (): void => {
    const { orgId, list, dispatchDownloadList } = this.props;

    if (orgId && list.id) {
      dispatchDownloadList(orgId, list.id, list.label, list.type);
    }
  };

  handleDeleteList = (data?: ListData): void => {
    const { onDelete, dispatchDeleteList, dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;
    const orgId = data && data.orgId;
    const listId = data && data.listId;
    const label = data ? data.label : '';

    if (orgId && listId) {
      dispatchShowConfirmationModal({
        title: __t('lists_context_menu.modal.delete_list_title'),
        message: __t('lists_context_menu.modal.delete_list_message', {
          msgPlaceholder: `<strong class="lists-view__modal-message">${label}</strong>`
        }),
        onConfirm: () => {
          dispatchDeleteList(orgId, listId)
            .then(onDelete);

          dispatchHideConfirmationModal();
        },
        onCancel: () => {
          dispatchHideConfirmationModal();
        }
      });
    }
  };

  render(): React.ReactNode {
    const { orgId, list, canManageList } = this.props;
    const menuItems = this.getContextMenuItems();
    const isDisabled = !canManageList && list.numCreators === 0;

    // we must have at least one item so the "more" button appears. since it is disabled,
    // it doesn't matter that it is actually empty. disabling makes the UX appear cleaner.
    const updatedMenuItems = isEmpty(menuItems) && isDisabled ? [{}] : menuItems;

    return (
      <div
        onClick={this.handleResidualClick}
      >
        {
          orgId && (
            <ContextMenu
              contextMenuProps={{
                items: updatedMenuItems,
                data: {
                  orgId,
                  listId: list.id,
                  label: list.label
                }
              }}
              width={40}
              disabled={isDisabled}
            />
          )
        }
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDownloadList: (orgId: number, listId: number, label: string, type: ListType) => (
    dispatch(downloadList(orgId, listId, label, type))
  ),

  dispatchDeleteList: (orgId: number, listId: number) => (
    dispatch(deleteList(orgId, listId))
  ),

  dispatchShowConfirmationModal: (props: ListConfirmModalProps) => (
    dispatch(showConfirmModal(props))
  ),

  dispatchHideConfirmationModal: () => (
    dispatch(hideConfirmModal())
  )
});

export default connect(null, mapDispatchToProps)(ListsContextMenu);
